import { Button } from "../../components/Button";
import { BackButton } from "../../components/BackButton";

export interface SubjectScore {
  subject: string;
  score: number;
  outOf: number;
  /** Theory questions answered but not auto-markable. */
  theoryAnswered?: number | undefined;
}

interface MockResultsScreenProps {
  /** JAMB scale: four subjects, 100 each. */
  total: number;
  outOf: number;
  deltaFromLast: number | null;
  subjects: SubjectScore[];
  readinessImpact?: { subject: string; before: number; after: number } | undefined;
  wrongCount: number;
  skippedCount: number;
  /** Theory questions answered but not auto-markable. */
  theoryCount?: number | undefined;
  onReviewWrong?: (() => void) | undefined;
  onDone?: (() => void) | undefined;
}

/**
 * Screen 12 — the JAMB-scale score, then what it changed.
 *
 * The readiness impact is the point: a mock score is a snapshot, readiness is
 * the thing that moves. Skipped questions are counted separately from wrong
 * ones because they mean different things — one is a gap, the other is a pace
 * problem. Theory questions are their own category: answered, but needing
 * human review to score.
 */
export function MockResultsScreen({
  total,
  outOf,
  deltaFromLast,
  subjects,
  readinessImpact,
  wrongCount,
  skippedCount,
  theoryCount = 0,
  onReviewWrong,
  onDone,
}: MockResultsScreenProps) {
  return (
    <div className="app-column px-5 pb-6 pt-6">
      <BackButton to="/practice" />

      <p className="m-0 text-[12px] uppercase tracking-[0.12em] text-text-muted">
        Mock done — JAMB scale
      </p>

      <div className="flex items-baseline gap-2 pt-2">
        <span className="tabular font-[family-name:var(--font-display)] text-[46px] font-bold leading-none">
          {total}
        </span>
        <span className="tabular text-[16px] text-text-muted">/ {outOf}</span>
      </div>
      {deltaFromLast !== null ? (
        <p className="tabular m-0 pt-1 text-[13px] text-text-muted">
          {deltaFromLast >= 0 ? "Up" : "Down"} {Math.abs(deltaFromLast)} from your last mock.
        </p>
      ) : (
        <p className="m-0 pt-1 text-[13px] text-text-muted">
          Your first mock — this is the baseline.
        </p>
      )}

      <ul className="m-0 flex list-none flex-col gap-2.5 p-0 pt-6">
        {subjects.map((entry) => (
          <li key={entry.subject} className="flex flex-col gap-1.5">
            <div className="flex items-baseline justify-between">
              <span className="text-[13.5px] capitalize">{entry.subject}</span>
              <span className="tabular text-[13px] text-text-muted">
                {entry.score} / {entry.outOf}
              </span>
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-[var(--radius-chip)] bg-surface-high">
              <div
                className="h-full rounded-[var(--radius-chip)] bg-accent"
                style={{ width: `${entry.outOf > 0 ? (entry.score / entry.outOf) * 100 : 0}%` }}
              />
            </div>
            {entry.theoryAnswered ? (
              <p className="m-0 text-[11px] text-text-muted">
                {entry.theoryAnswered} theory{" "}
                {entry.theoryAnswered === 1 ? "question" : "questions"} answered — needs manual
                review
              </p>
            ) : null}
          </li>
        ))}
      </ul>

      {readinessImpact ? (
        <p className="tabular m-0 mt-6 rounded-[var(--radius-card)] border border-border bg-surface p-4 text-[13px]">
          This mock updated your readiness —{" "}
          <span className="font-semibold">
            {readinessImpact.subject} {readinessImpact.before}% → {readinessImpact.after}%
          </span>{" "}
          <span className="text-positive">▲</span>
        </p>
      ) : null}

      <div className="mt-6 flex flex-col gap-1">
        <p className="tabular m-0 text-[12px] text-text-muted">
          Wrong · {wrongCount}
          {skippedCount > 0 ? <> &nbsp;·&nbsp; Skipped · {skippedCount}</> : null}
        </p>
        {theoryCount > 0 ? (
          <p className="m-0 text-[12px] text-text-muted">
            Theory · {theoryCount} answered (not auto-marked)
          </p>
        ) : null}
      </div>

      <div className="mt-auto flex flex-col gap-3 pt-8">
        <Button onClick={onReviewWrong}>Review my wrong answers</Button>
        <Button variant="ghost" onClick={onDone}>
          Done
        </Button>
      </div>
    </div>
  );
}
